// @flow
import React, {useState, useEffect, Fragment, useRef} from "react";
import {useObserver} from "mobx-react-lite";
import {MenuOutlined, SettingOutlined} from "@ant-design/icons";
import {Row, Col, Switch, Drawer, Menu} from "antd";

import {
    courierTypes,
    courierStatusColors,
    mapCourierStatuses,
    receiverColor,
    pickupColor,
    cities
} from "../../../constants/constants";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import {CourierLeafletMap} from "./CourierLeafletMap";
import {
    SelectField,
    FilterRow,
    LeftCornerContainer,
    SwitchContainer,
    MapContainer,
    LeftTopCornerContainer,
    FilterRowContainer,
    RightTopCornerContainer,
    MobileFilterButton,
    MobileMenuButton,
    DensityButton
} from "../dashboardStyles";
import {useTranslation} from "react-i18next";
import {
    CityFilter,
    CompanyFilter,
    PoolIntegrationTypeFilter
} from "../../_defaultComponents/FilterComponents";
import {CourierStatsWidget} from "./CourierStatsWidget/CourierStatsWidget";
import {PoolDensity} from "../../poolDensity/PoolDensity";
import {TileLayerControl} from "../../_defaultComponents/TileLayerControl/TileLayerControl";

export const CourierMapTab = ({isFocused, recall}) => {
    const stores = useStore();
    const {t, i18n} = useTranslation(["mapCourierStatuses", "courierMapTab", "pools"]);
    const [companyList, setCompanyList] = useState([]);
    const [courierFilterList, setCourierFilterList] = useState([]);
    const [poolGroups, setPoolGroups] = useState([]);
    const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
    const [menuDrawerVisible, setMenuDrawerVisible] = useState(false);
    const [densityDrawerVisible, setDensityDrawerVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [integrationType, setIntegrationType] = useState("");
    const mapInstanceRef = useRef(null);
    const tileLayerRef = useRef(null);

    const [, courierData] = useFetch({
        fetchFn: stores.courierStore.getCourierMapList.bind(null, {
            city: stores.meStore.map.filters.city,
            types: stores.meStore.map.filters.courierType,
            statuses:
                stores.meStore.map.filters.courierStatus.length === 1 &&
                stores.meStore.map.filters.courierStatus[0] === "available"
                    ? ["ready", "available"]
                    : stores.meStore.map.filters.courierStatus,
            dedicatedCompanyId: stores.meStore.map.filters.company,
            dedicatedSubCompanies: stores.meStore.map.filters.subCompany
                ? [stores.meStore.map.filters.subCompany]
                : [],
            dedicatedPoolIds: poolGroups
        }),
        waitForResponse: true,
        dependencies: [
            recall,
            JSON.stringify(stores.meStore.map.filters.city),
            JSON.stringify(stores.meStore.map.filters.courierType),
            JSON.stringify(stores.meStore.map.filters.courierStatus),
            JSON.stringify(stores.meStore.map.filters.company),
            JSON.stringify(stores.meStore.map.filters.subCompany),
            JSON.stringify(poolGroups)
        ],
        condition: stores.meStore.map.filters.city && stores.meStore.map.filters.city !== "all"
    });

    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: stores.meStore.map.filters.company,
            city: stores.meStore.map.filters.city,
            limit: 1000
        }),
        condition:
            (["", "pool"].indexOf(stores.meStore.map.filters.company) === -1 ||
                stores.meStore.checkRole("isCompanyUser")) &&
            !!stores.meStore.map.filters.company,
        dependencies: [stores.meStore.map.filters.company, stores.meStore.map.filters.city],
        initialCall:
            ["", "pool"].indexOf(stores.meStore.map.filters.company) === -1 ||
            stores.meStore.checkRole("isCompanyUser")
    });

    const [
        subCompanyMarkerListLoading,
        subCompanyMarkerList,
        setSubCompanyMarkerList,
        setSubCompanyMarkerListLoading
    ] = useFetch({
        fetchFn: stores.companyStore.getSubCompanyMapList.bind(null, {
            companyId:
                stores.meStore.map.filters.company === "pool"
                    ? ""
                    : stores.meStore.map.filters.company,
            disabled: stores.meStore.map.filters.disabled,
            city: stores.meStore.map.filters.city
        }),
        initialCall: true,
        dependencies: [
            stores.meStore.map.options.showSubCompanies,
            stores.meStore.map.filters.company === "pool" ? "" : stores.meStore.map.filters.company,
            stores.meStore.map.filters.city,
            stores.meStore.map.filters.disabled
        ],
        condition: stores.meStore.map.filters.city && stores.meStore.map.filters.city !== "all"
    });

    const [loading, pools] = useFetch({
        fetchFn: stores.poolStore.getPoolList.bind(null, {
            limit: 1000,
            page: 1,
            city: stores.meStore.map.filters.city
        }),
        initialCall: true,
        dependencies: [stores.meStore.map.filters.city]
    });

    const getCourierData = () => {
        let list = courierData?.courierStateList;
        if (stores.meStore.map.filters.courierType.length) {
            list = courierData?.courierStateList?.filter(courierState =>
                stores?.meStore?.map?.filters?.courierType?.includes(courierState.type)
            );
        }
        if (stores?.meStore?.map?.filters?.courierStatus?.length) {
            list = list?.filter(courierState => {
                if (courierState?.status === "busy") {
                    if (stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnPickup")) {
                        return courierState?.deliveryState?.includes("PickUp");
                    } else if (
                        stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnDelivery")
                    ) {
                        return courierState?.deliveryState?.includes("Delivery");
                    } else if (
                        stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnReturn")
                    ) {
                        return courierState?.deliveryState?.includes("Return");
                    } else if (
                        stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnTransfer")
                    ) {
                        return courierState?.deliveryState?.includes("Transfer");
                    } else {
                        return ["assigned", "waiting"].includes(courierState?.deliveryState);
                    }
                } else {
                    return stores?.meStore?.map?.filters?.courierStatus?.includes(
                        courierState?.status
                    );
                }
            });
        }
        if (stores?.meStore?.map?.filters?.company) {
            if (stores?.meStore?.map?.filters?.company === "pool") {
                list = list?.filter(courierState => !courierState?.dedicatedCompanyId);
            } else {
                const workType = stores?.appStore?.companyListFilter?.find(
                    x => x._id == stores?.meStore?.map?.filters?.company
                )?.workType;

                if (!poolGroups?.length) {
                    list = list?.filter(courierState => {
                        if (workType === "pool" && courierState?.delivery?.company?._id) {
                            return (
                                courierState?.delivery?.company?._id ===
                                stores?.meStore?.map?.filters?.company
                            );
                        }
                        return (
                            courierState?.delivery?.company?._id ===
                                stores?.meStore?.map?.filters?.company ||
                            courierState?.dedicatedCompanyId ===
                                stores?.meStore?.map?.filters?.company ||
                            (workType === "pool" ? !courierState?.dedicatedCompanyId : false)
                        );
                    });
                }
            }
        }
        if (stores?.meStore?.checkRole("isCompanyUser")) {
            list = list?.filter(
                courierState =>
                    !courierState?.isDummy ||
                    (courierState?.isDummy &&
                        !["available", "ready"].includes(courierState?.status))
            );
        }
        return list;
    };

    useEffect(() => {
        setCompanyList(
            stores.appStore.companyListFilter.map(val => {
                return {companyId: val._id, companyName: val.companyName};
            })
        );
    }, [stores.appStore.companyListFilter]);

    useEffect(() => {
        if (stores.meStore.map.filters.company == "") {
            stores.meStore.map.filters.subCompany = "";
            setSubCompanies([]);
        }
    }, [stores.meStore.map.filters.company]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Sayfa ilk yüklendiğinde şehir filtresine göre harita merkezini ayarla
    useEffect(() => {
        const currentCity = stores.meStore.map.filters.city;
        if (currentCity && currentCity !== "all") {
            const cityData = cities.find(c => c.value === currentCity);
            if (cityData && cityData.center) {
                stores.meStore.map.settings.center = cityData.center;
                stores.meStore.map.settings.zoom = cityData.zoom || 11;
            }
        } else if (currentCity === "all") {
            stores.meStore.map.settings.center = {lat: 39.49, lng: 33.88};
            stores.meStore.map.settings.zoom = 7;
        }
    }, []); // Sadece mount'ta çalış

    const onSearchName = value => {
        const lowerValue = value.toLowerCase();
        if (value.length < stores.meStore.map.filters.courier.length) {
            stores.meStore.map.filters.courier = value;
        }
        if (value.length > 2) {
            stores.meStore.map.filters.courier = value;
            const list = getCourierData()?.filter(
                courierState =>
                    courierState?.courier?.fullname.toLowerCase().includes(lowerValue) &&
                    courierState?.location?.coordinates[1] !== 0 &&
                    courierState?.location?.coordinates[0] !== 0
            );
            setCourierFilterList(list);
        }
    };

    const onBlurName = () => {
        stores.meStore.map.filters.courier = "";
        setCourierFilterList([]);
    };

    const onChangeName = value => {
        const lowerValue = value.toLowerCase();
        let tempCenter = {};
        stores.meStore.map.filters.courier = value;
        const filteredMap = courierFilterList.filter(
            courierState => courierState?.courier?.fullname.toLowerCase() === lowerValue
        );
        tempCenter.lat = filteredMap[0]?.location?.coordinates[1];
        tempCenter.lng = filteredMap[0]?.location?.coordinates[0];
        if (tempCenter.lat && tempCenter.lng) {
            stores.meStore.map.settings.center = tempCenter;
            stores.meStore.map.settings.zoom = 20;
        }
    };

    const onSelectName = value => {
        stores.meStore.map.filters.courier = value;
    };

    const isSubcompanyPoolSelected = subcompany => {
        if (!poolGroups || poolGroups?.length === 0 || !pools || !pools?.data) {
            return true;
        }

        const selectedPools = pools?.data.filter(p => poolGroups.includes(p._id.toString()));

        if (
            selectedPools.some(pool =>
                pool.subCompanies.some(c => c._id.toString() === subcompany._id.toString())
            )
        ) {
            return true;
        }
    };

    const {company: companyFilter, subCompany: subCompanyFilter} = stores.meStore.map.filters;
    const {companies: authCompanies, subcompanies: authSubCompanies} = stores.meStore.auth;
    const userSubCompanies = subCompanyMarkerList.filter(subcompanyMarker => {
        if (subCompanyFilter !== "") {
            return subcompanyMarker._id.toString() === subCompanyFilter;
        }
        if (companyFilter !== "") {
            return subcompanyMarker.companyId.toString() === companyFilter;
        }
        if (!stores.meStore.checkRole("isCompanyUser")) {
            return true;
        }
        if (
            [
                "cUser",
                "oUser",
                "sOperation",
                "sChef",
                "fieldManager",
                "bm",
                "agency",
                "sy"
            ].includes(stores.meStore.role)
        ) {
            if (authSubCompanies?.length > 0)
                return authSubCompanies.includes(subcompanyMarker._id);
            if (authCompanies?.length > 0)
                return authCompanies.includes(subcompanyMarker.companyId);
        }
        return false;
    });

    const getSubCompanyStatusType = type => {
        if (stores.meStore.map.filters.disabled === "all") {
            return true;
        }

        if (stores.meStore.map.filters.disabled === "true") {
            return type === "inactive";
        }

        if (stores.meStore.map.filters.disabled === "false") {
            return type === "active";
        }

        return false;
    };

    const handleSubcompanyType = (value, type) => {
        const {disabled} = stores.meStore.map.filters;

        if (type === "active") {
            if (value) {
                stores.meStore.map.filters.disabled = disabled === "true" ? "all" : "false";
            } else {
                stores.meStore.map.filters.disabled = disabled === "all" ? "true" : "all";
            }
        } else if (type === "inactive") {
            if (value) {
                stores.meStore.map.filters.disabled = disabled === "false" ? "all" : "true";
            } else {
                stores.meStore.map.filters.disabled = disabled === "all" ? "false" : "all";
            }
        }
    };

    return useObserver(() => (
        <>
            {isMobile && (
                <>
                    <MobileFilterButton
                        icon={<MenuOutlined />}
                        onClick={() => setFilterDrawerVisible(true)}
                        data-testid="mobile-filter-button"
                    >
                        Filtreler
                    </MobileFilterButton>
                    {courierData?.poolShiftDetails && courierData?.poolShiftDetails !== null && (
                        <DensityButton
                            onClick={() => setDensityDrawerVisible(true)}
                            visible={densityDrawerVisible}
                            icon={<MenuOutlined />}
                        >
                            Yoğunluk
                        </DensityButton>
                    )}
                    <Drawer
                        title="Yoğunluk"
                        onClose={() => setDensityDrawerVisible(false)}
                        visible={densityDrawerVisible}
                        height="auto"
                        bodyStyle={{padding: "12px"}}
                    >
                        {courierData?.poolShiftDetails &&
                            courierData?.poolShiftDetails !== null && (
                                <PoolDensity
                                    poolShiftDetails={courierData?.poolShiftDetails}
                                    isMobile={isMobile}
                                />
                            )}
                    </Drawer>
                    <MobileMenuButton
                        icon={<SettingOutlined />}
                        onClick={() => setMenuDrawerVisible(true)}
                        data-testid="mobile-menu-button"
                    />

                    <Drawer
                        title="Filtreler"
                        placement="top"
                        onClose={() => setFilterDrawerVisible(false)}
                        visible={filterDrawerVisible}
                        height="auto"
                        bodyStyle={{padding: "12px"}}
                        scrollToBottom={true}
                    >
                        <FilterRow gutter={[20, 20]} data-test-id="filter">
                            <Col xs={24}>
                                <CityFilter
                                    showAllOption={false}
                                    value={stores.meStore.map.filters.city}
                                    onChange={(value, option) => {
                                        stores.meStore.map.filters.city = value;
                                        stores.meStore.map.settings.center =
                                            value == "all"
                                                ? {lat: 39.49, lng: 33.88}
                                                : option["data-center"];
                                        stores.meStore.map.settings.zoom = value == "all" ? 7 : 11;
                                        setPoolGroups([]);
                                    }}
                                />
                            </Col>
                            {!stores.meStore.checkRole("isNonApiIntegratedCustomer") && (
                                <>
                                    <Col xs={24}>
                                        <h5>{t("fields.courierType", {ns: "common"})}</h5>
                                        <SelectField
                                            mode="multiple"
                                            data-testid="courierTypeSelect"
                                            value={stores.meStore.map.filters.courierType}
                                            placeholder={t("filters.addFilter", {ns: "common"})}
                                            onChange={value =>
                                                (stores.meStore.map.filters.courierType = value)
                                            }
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .indexOf(input.toLowerCase()) >= 0
                                            }
                                            filterSort={(optionA, optionB) =>
                                                optionA.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .localeCompare(
                                                        optionB.children.props.children
                                                            .toString()
                                                            .toLowerCase()
                                                    )
                                            }
                                        >
                                            {courierTypes.map(type => (
                                                <SelectField.Option
                                                    key={type.value}
                                                    value={type.value}
                                                    data-testid="courierTypeSelect_option"
                                                >
                                                    <span>
                                                        {t(type.translation_key, {
                                                            ns: "courierTypes"
                                                        })}
                                                    </span>
                                                </SelectField.Option>
                                            ))}
                                        </SelectField>
                                    </Col>
                                    <Col xs={24}>
                                        <h5>{t("fields.courierStatus", {ns: "common"})}</h5>
                                        <SelectField
                                            mode="multiple"
                                            value={stores.meStore.map.filters.courierStatus}
                                            placeholder={t("filters.addFilter", {ns: "common"})}
                                            onChange={value =>
                                                (stores.meStore.map.filters.courierStatus = value)
                                            }
                                            optionFilterProp="children"
                                            data-testid="courierStatusSelect"
                                            filterOption={(input, option) =>
                                                option.children.props.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .indexOf(input.toLowerCase()) >= 0
                                            }
                                            filterSort={(optionA, optionB) =>
                                                optionA.children.props.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .localeCompare(
                                                        optionB.children.props.children.props.children
                                                            .toString()
                                                            .toLowerCase()
                                                    )
                                            }
                                        >
                                            {mapCourierStatuses.map(type => (
                                                <SelectField.Option
                                                    key={type.value}
                                                    value={type.value}
                                                    data-testid="courierStatusSelect_option"
                                                >
                                                    <Row justify="space-between">
                                                        <span>
                                                            {t(type.translation_key, {
                                                                ns: "mapCourierStatuses"
                                                            })}
                                                        </span>
                                                    </Row>
                                                </SelectField.Option>
                                            ))}
                                        </SelectField>
                                    </Col>
                                </>
                            )}
                            {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                                <Col xs={24}>
                                    <CompanyFilter
                                        showAllOption={true}
                                        value={stores.meStore.map.filters.company}
                                        onChange={value => {
                                            value = value ?? "";
                                            stores.meStore.map.filters.company = value;
                                        }}
                                    />
                                </Col>
                            )}
                            {
                                <Col
                                    xs={24}
                                    order={stores.meStore.checkRole("isCompanyUser") ? 1 : 2}
                                >
                                    <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                                    <SelectField
                                        allowClear
                                        showSearch
                                        data-testid="subcompanySelect"
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        value={stores.meStore.map.filters.subCompany}
                                        onChange={value => {
                                            value = value ?? "";
                                            const targetSubcompany = subCompanies?.rows?.find(
                                                subcompany => subcompany._id == value
                                            );
                                            if (targetSubcompany) {
                                                stores.meStore.map.settings.center = {
                                                    lat: targetSubcompany.location.latitude,
                                                    lng: targetSubcompany.location.longitude
                                                };
                                                stores.meStore.map.settings.zoom = 13;
                                            }
                                            stores.meStore.map.filters.subCompany = value;
                                        }}
                                        filterOption={(input, option) =>
                                            option.props.children
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0 ||
                                            option.props.value
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        <SelectField.Option
                                            value=""
                                            data-testid="subcompanySelect_option"
                                        >
                                            {t("filters.all", {ns: "common"})}
                                        </SelectField.Option>
                                        {subCompanies.rows &&
                                            subCompanies?.rows.map(subcompany => (
                                                <SelectField.Option
                                                    key={subcompany._id}
                                                    value={subcompany._id}
                                                    data-testid="subcompanySelect_option"
                                                >
                                                    {subcompany.name}
                                                </SelectField.Option>
                                            ))}
                                    </SelectField>
                                </Col>
                            }
                            <Col xs={24}>
                                <h5>{t("fields.courierName", {ns: "common"})}</h5>
                                <SelectField
                                    data-testid="courierNameSelect"
                                    showSearch
                                    onBlur={onBlurName}
                                    onSearch={e => onSearchName(e)}
                                    onChange={e => onChangeName(e)}
                                    onSelect={e => onSelectName(e)}
                                    value={stores.meStore.map.filters.courier}
                                    placeholder={t("filters.courierNamePlaceholder", {
                                        ns: "courierMapTab"
                                    })}
                                    showArrow={false}
                                    notFoundContent={null}
                                    defaultActiveFirstOption={false}
                                    filterOption={false}
                                >
                                    {courierFilterList.map(courier => (
                                        <SelectField.Option
                                            data-testid="courierNameSelect_option"
                                            key={courier.courier._id}
                                            value={courier.courier.fullname}
                                        >
                                            {courier.courier.fullname}
                                        </SelectField.Option>
                                    ))}
                                </SelectField>
                            </Col>
                            {!stores.meStore.checkRole("isCompanyUser") && (
                                <>
                                    <Col xs={24}>
                                        <h5>{t("fields.poolRegion", {ns: "pools"})}</h5>
                                        <SelectField
                                            mode="multiple"
                                            data-testid="poolGroupSelect"
                                            value={poolGroups}
                                            placeholder={t("filters.addFilter", {ns: "common"})}
                                            onChange={value => setPoolGroups(value)}
                                        >
                                            {pools?.data
                                                ? pools?.data.map(pool => (
                                                      <SelectField.Option
                                                          key={pool._id}
                                                          value={pool._id}
                                                          data-testid="poolGroups_option"
                                                      >
                                                          {pool.name}
                                                      </SelectField.Option>
                                                  ))
                                                : ""}
                                        </SelectField>
                                    </Col>
                                    <Col xs={24}>
                                        <h5>Entegrasyon Tipi</h5>
                                        <PoolIntegrationTypeFilter
                                            value={integrationType}
                                            onChange={value => setIntegrationType(value)}
                                        />
                                    </Col>
                                </>
                            )}
                        </FilterRow>
                    </Drawer>
                    <Drawer
                        placement="left"
                        onClose={() => setMenuDrawerVisible(false)}
                        visible={menuDrawerVisible}
                        width="250px"
                    >
                        <Menu mode="vertical">
                            {stores.meStore.role !== "cUser" && (
                                <Menu.Item key="subcompany">
                                    <SwitchContainer label={t("fields.subcompany", {ns: "common"})}>
                                        <Switch
                                            size="small"
                                            data-testid="optionList_subCompanySwitch"
                                            checked={stores.meStore.map.options.showSubCompanies}
                                            onChange={e => {
                                                stores.meStore.map.options.showSubCompanies = e;
                                                stores.meStore.map.filters.disabled = e
                                                    ? "false"
                                                    : "all";
                                            }}
                                        />
                                    </SwitchContainer>
                                </Menu.Item>
                            )}
                            {stores.meStore.role === "cUser" &&
                                (stores.meStore.map.options.showSubCompanies = true)}

                            <Fragment>
                                <div style={{marginTop: "8px"}}>
                                    {mapInstanceRef.current && tileLayerRef.current && (
                                        <TileLayerControl
                                            mapInstance={mapInstanceRef.current}
                                            tileLayerRef={tileLayerRef}
                                        />
                                    )}
                                </div>
                            </Fragment>
                        </Menu>
                    </Drawer>
                </>
            )}

            {!isMobile && (
                <>
                    <FilterRowContainer>
                        <FilterRow gutter={[20, 20]} data-test-id="filter">
                            <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                <CityFilter
                                    showAllOption={false}
                                    value={stores.meStore.map.filters.city}
                                    onChange={(value, option) => {
                                        stores.meStore.map.filters.city = value;
                                        stores.meStore.map.settings.center =
                                            value == "all"
                                                ? {lat: 39.49, lng: 33.88}
                                                : option["data-center"];
                                        stores.meStore.map.settings.zoom = value == "all" ? 7 : 11;
                                        setPoolGroups([]);
                                    }}
                                />
                            </Col>
                            {!stores.meStore.checkRole("isNonApiIntegratedCustomer") && (
                                <>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={3} order={2}>
                                        <h5>{t("fields.courierType", {ns: "common"})}</h5>
                                        <SelectField
                                            mode="multiple"
                                            data-testid="courierTypeSelect"
                                            value={stores.meStore.map.filters.courierType}
                                            placeholder={t("filters.addFilter", {ns: "common"})}
                                            onChange={value =>
                                                (stores.meStore.map.filters.courierType = value)
                                            }
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .indexOf(input.toLowerCase()) >= 0
                                            }
                                            filterSort={(optionA, optionB) =>
                                                optionA.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .localeCompare(
                                                        optionB.children.props.children
                                                            .toString()
                                                            .toLowerCase()
                                                    )
                                            }
                                        >
                                            {courierTypes.map(type => (
                                                <SelectField.Option
                                                    key={type.value}
                                                    value={type.value}
                                                    data-testid="courierTypeSelect_option"
                                                >
                                                    <span>
                                                        {t(type.translation_key, {
                                                            ns: "courierTypes"
                                                        })}
                                                    </span>
                                                </SelectField.Option>
                                            ))}
                                        </SelectField>
                                    </Col>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={3} order={2}>
                                        <h5>{t("fields.courierStatus", {ns: "common"})}</h5>
                                        <SelectField
                                            mode="multiple"
                                            value={stores.meStore.map.filters.courierStatus}
                                            placeholder={t("filters.addFilter", {ns: "common"})}
                                            onChange={value =>
                                                (stores.meStore.map.filters.courierStatus = value)
                                            }
                                            optionFilterProp="children"
                                            data-testid="courierStatusSelect"
                                            filterOption={(input, option) =>
                                                option.children.props.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .indexOf(input.toLowerCase()) >= 0
                                            }
                                            filterSort={(optionA, optionB) =>
                                                optionA.children.props.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .localeCompare(
                                                        optionB.children.props.children.props.children
                                                            .toString()
                                                            .toLowerCase()
                                                    )
                                            }
                                        >
                                            {mapCourierStatuses.map(type => (
                                                <SelectField.Option
                                                    key={type.value}
                                                    value={type.value}
                                                    data-testid="courierStatusSelect_option"
                                                >
                                                    <Row justify="space-between">
                                                        <span>
                                                            {t(type.translation_key, {
                                                                ns: "mapCourierStatuses"
                                                            })}
                                                        </span>
                                                    </Row>
                                                </SelectField.Option>
                                            ))}
                                        </SelectField>
                                    </Col>
                                </>
                            )}

                            {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                                <Col xs={12} sm={8} md={8} lg={4} xl={3} order={2}>
                                    <CompanyFilter
                                        showAllOption={true}
                                        value={stores.meStore.map.filters.company}
                                        onChange={value => {
                                            value = value ?? "";
                                            stores.meStore.map.filters.company = value;
                                        }}
                                    />
                                </Col>
                            )}
                            {
                                <Col
                                    xs={12}
                                    sm={8}
                                    md={8}
                                    lg={4}
                                    xl={3}
                                    order={stores.meStore.checkRole("isCompanyUser") ? 1 : 2}
                                >
                                    <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                                    <SelectField
                                        allowClear
                                        showSearch
                                        data-testid="subcompanySelect"
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        value={stores.meStore.map.filters.subCompany}
                                        onChange={value => {
                                            value = value ?? "";
                                            const targetSubcompany = subCompanies?.rows?.find(
                                                subcompany => subcompany._id == value
                                            );
                                            if (targetSubcompany) {
                                                stores.meStore.map.settings.center = {
                                                    lat: targetSubcompany.location.latitude,
                                                    lng: targetSubcompany.location.longitude
                                                };
                                                stores.meStore.map.settings.zoom = 13;
                                            }
                                            stores.meStore.map.filters.subCompany = value;
                                        }}
                                        filterOption={(input, option) =>
                                            option.props.children
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0 ||
                                            option.props.value
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        <SelectField.Option
                                            value=""
                                            data-testid="subcompanySelect_option"
                                        >
                                            {t("filters.all", {ns: "common"})}
                                        </SelectField.Option>
                                        {subCompanies.rows &&
                                            subCompanies?.rows.map(subcompany => (
                                                <SelectField.Option
                                                    key={subcompany._id}
                                                    value={subcompany._id}
                                                    data-testid="subcompanySelect_option"
                                                >
                                                    {subcompany.name}
                                                </SelectField.Option>
                                            ))}
                                    </SelectField>
                                </Col>
                            }
                            <Col xs={12} sm={8} md={8} lg={4} xl={3} order={2}>
                                <h5>{t("fields.courierName", {ns: "common"})}</h5>
                                <SelectField
                                    data-testid="courierNameSelect"
                                    showSearch
                                    onBlur={onBlurName}
                                    onSearch={e => onSearchName(e)}
                                    onChange={e => onChangeName(e)}
                                    onSelect={e => onSelectName(e)}
                                    value={stores.meStore.map.filters.courier}
                                    placeholder={t("filters.courierNamePlaceholder", {
                                        ns: "courierMapTab"
                                    })}
                                    showArrow={false}
                                    notFoundContent={null}
                                    defaultActiveFirstOption={false}
                                    filterOption={false}
                                >
                                    {courierFilterList.map(courier => (
                                        <SelectField.Option
                                            data-testid="courierNameSelect_option"
                                            key={courier.courier._id}
                                            value={courier.courier.fullname}
                                        >
                                            {courier.courier.fullname}
                                        </SelectField.Option>
                                    ))}
                                </SelectField>
                            </Col>
                            {!stores.meStore.checkRole("isCompanyUser") && (
                                <>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={3} order={2}>
                                        <h5>{t("fields.poolRegion", {ns: "pools"})}</h5>
                                        <SelectField
                                            mode="multiple"
                                            data-testid="poolGroupSelect"
                                            value={poolGroups}
                                            placeholder={t("filters.addFilter", {ns: "common"})}
                                            onChange={value => setPoolGroups(value)}
                                        >
                                            {pools?.data
                                                ? pools?.data.map(pool => (
                                                      <SelectField.Option
                                                          key={pool._id}
                                                          value={pool._id}
                                                          data-testid="poolGroups_option"
                                                      >
                                                          {pool.name}
                                                      </SelectField.Option>
                                                  ))
                                                : ""}
                                        </SelectField>
                                    </Col>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={3} order={2}>
                                        <h5>Entegrasyon Tipi</h5>
                                        <PoolIntegrationTypeFilter
                                            value={integrationType}
                                            onChange={value => setIntegrationType(value)}
                                            disabled={poolGroups?.length === 0}
                                        />
                                    </Col>
                                </>
                            )}
                        </FilterRow>
                        <LeftTopCornerContainer>
                            <SwitchContainer label={t("fields.subcompany", {ns: "common"})}>
                                <Switch
                                    size="small"
                                    data-testid="optionList_subCompanySwitch"
                                    checked={
                                        userSubCompanies?.length === 1
                                            ? (stores.meStore.map.options.showSubCompanies = true)
                                            : stores.meStore.map.options.showSubCompanies
                                    }
                                    onChange={e => {
                                        stores.meStore.map.options.showSubCompanies = e;
                                        stores.meStore.map.filters.disabled = e ? "false" : "all";
                                    }}
                                />
                            </SwitchContainer>
                        </LeftTopCornerContainer>
                        <RightTopCornerContainer>
                            {courierData?.poolShiftDetails &&
                                courierData?.poolShiftDetails !== null && (
                                    <PoolDensity poolShiftDetails={courierData?.poolShiftDetails} />
                                )}
                        </RightTopCornerContainer>
                    </FilterRowContainer>

                    <LeftCornerContainer data-testid="optionList">
                        <Fragment>
                            <div style={{marginTop: "8px"}}>
                                {mapInstanceRef.current && tileLayerRef.current && (
                                    <TileLayerControl
                                        mapInstance={mapInstanceRef.current}
                                        tileLayerRef={tileLayerRef}
                                    />
                                )}
                            </div>
                        </Fragment>
                    </LeftCornerContainer>
                </>
            )}

            {(stores.meStore.checkRole("notVisibleForCompanyUser") ||
                stores.meStore.checkRole("sChef")) && (
                <>
                    <CourierStatsWidget
                        courierStats={getCourierData()
                            ?.filter(courierState => {
                                if (!stores?.meStore?.map?.options?.showDummyCouriers) {
                                    if (stores?.meStore?.map?.filters?.subCompany !== "") {
                                        return (
                                            (courierState?.courier?.dedicatedSubCompanies?.indexOf(
                                                stores?.meStore?.map?.filters?.subCompany
                                            ) !== -1 ||
                                                courierState?.delivery?.pickupInfos?.some(
                                                    pickupInfo =>
                                                        pickupInfo?._id?.toString() ===
                                                        stores?.meStore?.map?.filters?.subCompany
                                                )) &&
                                            !courierState?.courier?.isDummy
                                        );
                                    }
                                    return !courierState?.isDummy;
                                } else {
                                    return courierState;
                                }
                            })
                            ?.filter(courierState => {
                                if (!integrationType) return true;
                                const courierIntegrationType = courierState.integrationType;
                                return courierIntegrationType === integrationType;
                            })
                            ?.map((courierState, i) => {
                                const courierStatus =
                                    courierState?.deliveryState || courierState?.status;
                                return {...courierState, courierStatus};
                            })}
                        poolShiftDetails={courierData?.poolShiftDetails}
                        poolGroups={poolGroups}
                        selectedPool={courierData?.filteredPools}
                        integrationType={integrationType}
                    />
                </>
            )}

            <MapContainer
                data-testid="map"
                style={isMobile ? {height: "calc(100vh - 60px)", width: "100%"} : {}}
            >
                <CourierLeafletMap
                    getCourierData={getCourierData}
                    subCompanyMarkerList={subCompanyMarkerList}
                    companyList={companyList}
                    userSubCompanies={userSubCompanies}
                    integrationType={integrationType}
                    isSubcompanyPoolSelected={isSubcompanyPoolSelected}
                    mapInstanceRef={mapInstanceRef}
                    tileLayerRef={tileLayerRef}
                />
            </MapContainer>
        </>
    ));
};
